/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Režňák (reznak@cadwork.cz)
 *
 */

// jscs:disable requireCamelCaseOrUpperCaseIdentifiers
import RenderBinNode from "App/render/webgl/nodes/render_bin_node";
import WebGLRenderGraph from "App/render/webgl//render_graph";
import {mat4, vec2, vec3} from "gl-matrix";
import ArrowNarrow from "App/environment/objects/arrow_narrow";
import WebGLContext from "App/web_gl_context";
import LetterX from "App/environment/objects/letter_x";
import LetterY from "App/environment/objects/letter_y";
import LetterZ from "App/environment/objects/letter_z";
import Plane from "App/environment/objects/plane";
import {Tools} from "App/environment/tools";
import Pass from "App/render/webgl/passes/pass";
import Factory from "App/render/webgl/nodes/bins/factory";
import {HUDBinTypeName} from "App/render/webgl/nodes/bins/type_names";
import IScene from "js_interface/src/scene_interface";
//@ts-ignore we need the ts because otherwise the enum inside is not exported as tsx argue that no out is emmit for that file. We can use export const enum, but in this case tsc is ok with that, but on enum is exported at all
import {CompassTypeEnum} from "js_interface/src/modules/hud/scene_hud_enums.ts";
//@ts-ignore we need the ts because otherwise the enum inside is not exported as tsx argue that no out is emmit for that file. We can use export const enum, but in this case tsc is ok with that, but on enum is exported at all
import {EBackgroundImageFitType, EBackgroundType} from "js_interface/src/modules/background/scene_background_enums.ts";

/**
 * Head Up Display ... elements in "2D" space over 3D model scene
 *
 * @constructor
 */
export default
class HUD extends RenderBinNode {

    public static TypeName: string = HUDBinTypeName;

    private _initialized: boolean;
    private _lastViewSize: vec2;
    private _pgc: { eye: any; upDir: any; screenPos: vec2 };
    private _tmpVec3: vec3;
    private _tmpMat4: mat4;
    private _arrows: ArrowNarrow[];
    private _letterX: LetterX;
    private _letterY: LetterY;
    private _letterZ: LetterZ;
    private _compassCircle: Plane;
    private _compassCircleBack: Plane;
    private _compassPlus: Plane;
    private _compassMinus: Plane;
    private _compassType: CompassTypeEnum;
    private _compassOn: number;
    private _compassOff: number;
    private _backgroundImage: Plane;
    private _backgroundImageScaleValid: boolean;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph, null);

        this._initialized = false;
        this._lastViewSize = vec2.create();

        // Temporary variables
        this._pgc = {
            upDir: vec3.fromValues(0, 1, 0),
            eye: vec3.create(),
            screenPos: vec2.create()
        };

        this._tmpVec3 = vec3.create();
        this._tmpMat4 = mat4.create();

        /** @type [ArrowNarrow] */
        this._arrows = [];

        let gl = (this._context as WebGLContext).getWebGLRenderingContext();
        // GL Objects
        for (let i = 0; i < 3; ++i) {
            let arrow = new ArrowNarrow(this._sceneAPI, gl);
            arrow.setScaleUni(0.55);
            this._arrows.push(arrow);
        }

        this._arrows[0].setColor(1.0, 0.0, 0.0);
        this._arrows[1].setColor(0.0, 1.0, 0.0);
        this._arrows[2].setColor(0.0, 0.0, 1.0);

        this._letterX = new LetterX(this._sceneAPI, gl);
        this._letterX.setColor4(this._arrows[0].getColor());
        this._letterX.addColor(0, 0.5, 0.5);
        this._letterX.setScaleUni(0.15);

        this._letterY = new LetterY(this._sceneAPI, gl);
        this._letterY.setColor4(this._arrows[2].getColor());
        this._letterY.addColor(0, 0.5, 0.5);
        this._letterY.setScaleVec(this._letterX.getScale());

        this._letterZ = new LetterZ(this._sceneAPI, gl);
        this._letterZ.setColor4(this._arrows[1].getColor());
        this._letterZ.addColor(0, 0.5, 0.5);
        this._letterZ.setScaleVec(this._letterX.getScale());

        // Compass items
        this._compassCircle = new Plane(this._sceneAPI, gl);
        this._compassCircle.setScaleUni(0.65);

        this._compassCircleBack = new Plane(this._sceneAPI, gl);
        this._compassCircleBack.setScaleVec(this._compassCircle.getScale());

        this._compassPlus = new Plane(this._sceneAPI, gl);
        this._compassPlus.setScaleUni(0.12);
        this._compassPlus.setRotation(0, Math.PI, 0);

        this._compassMinus = new Plane(this._sceneAPI, gl);
        this._compassMinus.setScaleUni(0.12);
        this._compassMinus.setRotation(0, Math.PI, 0);

        this._compassType = null;
        this._compassOn = 1.0;
        this._compassOff = 0.85;

        // Background image
        this._backgroundImage = null;
    }

    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): HUD {
        return new HUD(aSceneAPI, aRenderGraph);
    }

    /**
     * Release
     */
    public release() {
        super.release();

        // Release GL Objects
        for (let arrow of this._arrows) {
            arrow.release();
        }
        this._arrows = [];

        // Letters
        if (this._letterX) {
            this._letterX.release();
            this._letterX = null;
        }
        if (this._letterY) {
            this._letterY.release();
            this._letterY = null;
        }
        if (this._letterZ) {
            this._letterZ.release();
            this._letterZ = null;
        }

        // Compass
        if (this._compassCircle) {
            this._compassCircle.release();
            this._compassCircle = null;
        }
        if (this._compassCircleBack) {
            this._compassCircleBack.release();
            this._compassCircleBack = null;
        }
        if (this._compassPlus) {
            this._compassPlus.release();
            this._compassPlus = null;
        }
        if (this._compassMinus) {
            this._compassMinus.release();
            this._compassMinus = null;
        }

        if (this._backgroundImage) {
            this._backgroundImage.release();
            this._backgroundImage = null;
        }
    }

    /**
     * @param {Pass} aRenderPass
     * @return {boolean}
     */
    public render(aRenderPass: Pass) {
        if (!this._canRender()) {
            return false;
        }

        /** @type WebGLRenderingContext */
        let gl = (this._context as WebGLContext).getWebGLRenderingContext();
        let view = aRenderPass.view;
        let backupShaderIndex = aRenderPass.getActiveShaderRenderableIndex();
        // let hwCameraModule = this._sceneAPI.hwcameraModule;

        // Clear depth before rendering, this needs depth test but is over all
        gl.clear(gl.DEPTH_BUFFER_BIT);

        // Reposition the elements
        if (!vec2.equals(this._lastViewSize, view.getSize()) || view.getHud().getArrowsNeedUpdate()) {
            view.getHud().recalculateSize();

            vec2.copy(this._lastViewSize, view.getSize());

            vec2.copy(this._pgc.screenPos, view.getHud().getHUDScreenToPoint(view.getHud().getCompassMargin()));
            let margin = this._pgc.screenPos;
            let hudSize = view.getHud().getSize();
            let halfSizeX = hudSize[0] / 2.0;
            let halfSizeY = hudSize[1] / 2.0;

            // Compass
            this._compassCircle.setPosition(halfSizeX - margin[0] - 0.9, halfSizeY - margin[1] - 0.5, 0);
            this._compassCircleBack.setPositionVec(this._compassCircle.getPosition());
            this._compassPlus.setPosition(halfSizeX - margin[0], halfSizeY - margin[1], 0);
            this._compassMinus.setPosition(halfSizeX - margin[0], halfSizeY - margin[1] - 0.28, 0);

            // Inform module for a change
            view.getHud().setCompassPosition(this._compassCircle.getPosition());

            // Arrows
            vec2.copy(this._pgc.screenPos, view.getHud().getHUDScreenToPoint(view.getHud().getArrowsMargin()));
            let arrowsPosition = vec3.fromValues(-halfSizeX + margin[0] + 1.1, -halfSizeY + margin[1] + 0.6, 0);
            for (let arrow of this._arrows) {
                arrow.setPositionVec(arrowsPosition);
            }
            view.getHud().setArrowsNeedUpdate(false);

            // Letters
            this._letterX.setPositionVec(arrowsPosition);
            this._letterY.setPositionVec(arrowsPosition);
            this._letterZ.setPositionVec(arrowsPosition);

            // Background size calculation
            this._backgroundImageScaleValid = false;
        }

        // Some helper objects
        if (view.getHud().isArrowsEnabled() && view.getHud().isArrowsVisible()) {
            this._renderArrows(aRenderPass);
        }

        if (view.getHud().isCompassEnabled() && view.getHud().isCompassVisible()) {
            this._setCompassTextures();
            this._renderCompass(aRenderPass);
        }

        // Restore values
        aRenderPass.setActiveShaderRenderableIndex(backupShaderIndex);
    }

    /**
     * @param {Pass} aRenderPass
     */
    public renderBackground(aRenderPass: Pass) {
        /* jshint camelcase:false */
        if (!this._canRender()) {
            return;
        }

        // Set texture
        // @ts-ignore
        let cameraModule = this._sceneAPI.hwcamerasimpleModule;
        let backgroundModule = this._sceneAPI.backgroundModule;
        let backgroundType = backgroundModule.getBackgroundType();

        if (backgroundType === EBackgroundType.IMAGE && backgroundModule.getBackgroundImageSource() === '' || !backgroundModule.isBackgroundImageEnabled()) {
            return;
        }

        // we can render also only part of the background image
        if (backgroundModule.getBackgroundImageOffsetAndSizeChanged()) {
            backgroundModule.setBackgroundImageOffsetAndSizeChanged(false);

            // update texture coordinates based on image offset and size
            let attributes: number[] = Plane.getDefaultAttributes();

            // update texture coordinates based on the background module
            let offsetAndSize = this._sceneAPI.getBackgroundModule().getBackgroundImageOffsetAndSizeRef();

            // 0 => [0,1]
            attributes[6] = offsetAndSize.offsetX;
            attributes[7] = 1.0 - offsetAndSize.offsetY;

            // 1 => [0,0]
            attributes[14] = offsetAndSize.offsetX;
            attributes[15] = 1.0 - (offsetAndSize.offsetY + offsetAndSize.height);

            // 2 => [1,0]
            attributes[22] = offsetAndSize.offsetX + offsetAndSize.width;
            attributes[23] = 1.0 - (offsetAndSize.offsetY + offsetAndSize.height);

            // 3 => [1,1]
            attributes[30] = offsetAndSize.offsetX + offsetAndSize.width;
            attributes[31] = 1.0 - offsetAndSize.offsetY;

            this._backgroundImage.updateAttributes(attributes);
        }

        // On change reload texture
        if (backgroundModule.wasBackgroundImageChanged()) {
            backgroundModule.setBackgroundImageChanged(false);
            this._backgroundImageScaleValid = false;

            // Texture or camera
            if (backgroundType === EBackgroundType.IMAGE) {
                this._backgroundImage.setTexture(
                    0,
                    backgroundModule.getBackgroundImageSourceBasePath() + backgroundModule.getBackgroundImageSource(),
                    false,
                    false,
                    false
                );
                return;
            } else {
                cameraModule.enable(backgroundModule.getBackgroundCameraIndex());
                this._sceneAPI.viewModeModule.scheduleRedraw();
            }
        }

        /** @type WebGLRenderingContext */
        let gl = (this._context as WebGLContext).getWebGLRenderingContext();
        let view = aRenderPass.view;

        let shader: any = aRenderPass.setShaderByIndex(1, this._typeName);

        // Disable depth test
        gl.disable(gl.DEPTH_TEST);

        // Set uniforms
        gl.uniformMatrix4fv(shader.uniformLocations.u_viewProjectionMatrix, false, view.getHud().getHUDMatrix());
        mat4.identity(this._tmpMat4);
        gl.uniformMatrix4fv(shader.uniformLocations.u_rotationMatrix, false, this._tmpMat4);

        // Use camera
        if (backgroundType === EBackgroundType.HWCAMERA) {
            this._backgroundImageScaleValid = true;

            if (cameraModule.isReady()) {
                this._sceneAPI.viewModeModule.scheduleRedraw();

                // Get device info
                let video = cameraModule.getVideoElement();

                // TODO Resize only on demand
                let hudSize = view.getHud().getSize();
                let textureSize = vec2.fromValues(video.videoWidth, video.videoHeight);
                let ratioWidth = hudSize[0] / textureSize[0];
                let ratioHeight = hudSize[1] / textureSize[1];
                let ratio = Math.min(ratioWidth, ratioHeight);

                // fill
                ratio = Math.max(ratioWidth, ratioHeight);
                // if (backgroundModule.getBackgroundImageScaleFit() === SceneAPI.EBackgroundImageFitType.FILL) {
                //     ratio = Math.max(ratioWidth, ratioHeight);
                // }
                // else {
                //     ratio = Math.min(ratioWidth, ratioHeight);
                // }

                this._backgroundImage.setScale((textureSize[0] * ratio) / 2.0, (textureSize[1] * ratio) / 2.0, 0);

                // Upload video frame as a texture
                gl.bindTexture(gl.TEXTURE_2D, this._backgroundImage.getTexture(0));
                gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);

                // TODO Redraw only this part, it is not necessary to render whole 3D model
                this._sceneAPI.viewModeModule.scheduleRedraw();
            } else {
                if (!cameraModule.isAvailable()) { // hide camera background when not available, but keep last image when disabled by user only
                    this._backgroundImage.setScale(0, 0, 0);
                }
            }

            // Render
            this._backgroundImage.draw(aRenderPass, 1);
        }
        // Use still image
        else {
            // Resize if needed
            if (!this._backgroundImageScaleValid) {
                let backgroundImage = this._backgroundImage.getImage(0);
                if (backgroundImage === null) {
                    return;
                }

                let hudSize = view.getHud().getSize();
                let textureSize = vec2.fromValues(backgroundImage.width, backgroundImage.height);

                let ratioWidth = hudSize[0] / textureSize[0];
                let ratioHeight = hudSize[1] / textureSize[1];
                let ratio = 1;

                if (backgroundModule.getBackgroundImageScaleFit() === EBackgroundImageFitType.FILL) {
                    ratio = Math.max(ratioWidth, ratioHeight);
                } else {
                    ratio = Math.min(ratioWidth, ratioHeight);
                }

                this._backgroundImage.setScale((textureSize[0] * ratio) / 2.0, (textureSize[1] * ratio) / 2.0, 0);
                this._backgroundImageScaleValid = true;
                this._sceneAPI.viewModeModule.scheduleRedraw();
            }

            gl.enable(gl.BLEND);
            gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE);
            gl.depthFunc(gl.LEQUAL);

            // Render
            this._backgroundImage.draw(aRenderPass, 1);

            gl.depthFunc(gl.LEQUAL);
            gl.disable(gl.BLEND);
        }

        shader.disableAttributes();
        gl.enable(gl.DEPTH_TEST);
    }

    /**
     * @param {Pass} aRenderPass Render uid to texture
     */
    public renderControls(aRenderPass: Pass) {
        /* jshint camelcase:false */
        /** @type WebGLRenderingContext */
        let gl = (this._context as WebGLContext).getWebGLRenderingContext();
        let view = aRenderPass.view;
        let camera = view.getCamera();
        let shader: any = aRenderPass.setShaderByIndex(2, this._typeName);

        // Compass
        // -------
        if (view.getHud().isCompassEnabled() && view.getHud().isCompassVisible()) {
            gl.uniformMatrix4fv(shader.uniformLocations.u_viewProjectionMatrix, false, view.getHud().getHUDMatrix());

            // Without rotation buttons
            mat4.identity(this._tmpMat4);
            gl.uniformMatrix4fv(shader.uniformLocations.u_rotationMatrix, false, this._tmpMat4);
            gl.uniform1f(shader.uniformLocations.u_id, this._compassPlus.getID());
            this._compassPlus.draw(aRenderPass, 1);

            gl.uniform1f(shader.uniformLocations.u_id, this._compassMinus.getID());
            this._compassMinus.draw(aRenderPass, 1);

            // Rotating compass
            let tilt = view.getHud().isCompassTiltEnabled();
            this._calcCompassRotation(this._tmpMat4, camera.getCameraDirection(), tilt);

            // Front
            gl.uniformMatrix4fv(shader.uniformLocations.u_rotationMatrix, false, this._tmpMat4);
            gl.uniform1f(shader.uniformLocations.u_id, this._compassCircle.getID());
            this._compassCircle.draw(aRenderPass, 1);

            // Back
            mat4.rotateY(this._tmpMat4, this._tmpMat4, Math.PI);
            gl.uniformMatrix4fv(shader.uniformLocations.u_rotationMatrix, false, this._tmpMat4);
            gl.uniform1f(shader.uniformLocations.u_id, this._compassCircleBack.getID());
            this._compassCircleBack.draw(aRenderPass, 1);
        }

        // axis cross
        // Set uniforms
        if (view.getHud().isArrowsEnabled() && view.getHud().isArrowsVisible()) {
            gl.uniformMatrix4fv(shader.uniformLocations.u_viewProjectionMatrix, false, view.getHud().getHUDMatrix());

            for (let i = 0; i < this._arrows.length; ++i) {
                let arrow = this._arrows[i];

                // Model rotation matrix
                vec3.set(this._tmpVec3, 0, 0, 0);
                mat4.lookAt(this._tmpMat4, this._pgc.eye, this._tmpVec3, this._pgc.upDir);
                if (i === 0) {
                    mat4.rotateX(this._tmpMat4, this._tmpMat4, Math.PI / 2.0);
                    mat4.rotateZ(this._tmpMat4, this._tmpMat4, -Math.PI / 2.0);
                } else if (i === 2) {
                    mat4.rotateX(this._tmpMat4, this._tmpMat4, -Math.PI / 2.0);
                }

                gl.uniformMatrix4fv(shader.uniformLocations.u_rotationMatrix, false, this._tmpMat4);
                gl.uniform1f(shader.uniformLocations.u_id, arrow.getID());
                arrow.draw(aRenderPass, 1);

                // Letter to axis
                vec3.set(this._tmpVec3, 0, 6, 0);
                switch (i) {
                    case 0:
                        mat4.translate(this._tmpMat4, this._tmpMat4, this._tmpVec3);
                        gl.uniformMatrix4fv(shader.uniformLocations.u_rotationMatrix, false, this._tmpMat4);
                        gl.uniform1f(shader.uniformLocations.u_id, this._letterX.getID());
                        this._letterX.draw(aRenderPass, 1);
                        break;
                    case 1:
                        mat4.translate(this._tmpMat4, this._tmpMat4, this._tmpVec3);
                        mat4.rotateX(this._tmpMat4, this._tmpMat4, Math.PI / 2.0);
                        gl.uniformMatrix4fv(shader.uniformLocations.u_rotationMatrix, false, this._tmpMat4);
                        gl.uniform1f(shader.uniformLocations.u_id, this._letterZ.getID());
                        this._letterZ.draw(aRenderPass, 1);
                        break;
                    case 2:
                        mat4.translate(this._tmpMat4, this._tmpMat4, this._tmpVec3);
                        mat4.rotateX(this._tmpMat4, this._tmpMat4, Math.PI); // rotate the letter back
                        gl.uniformMatrix4fv(shader.uniformLocations.u_rotationMatrix, false, this._tmpMat4);
                        gl.uniform1f(shader.uniformLocations.u_id, this._letterY.getID());
                        this._letterY.draw(aRenderPass, 1);
                        break;
                }
            }
        }

        shader.disableAttributes();
    }

    private _initCompassIdsPerView() {

        let ids = Tools.getInstance().getManipulatorManager().getID(4);

        // set same compass id for all views, but whis needs to be changes in future to be able to select each compass
        // in each view separately
        // we need to pass all views and set the arrows enabled
        let views = this._sceneAPI.viewModeModule.getViews();
        for (let v in views) {
            let view = views[v];
            view.getHud().setCompassID(ids);
        }

        this._sceneAPI.viewModeModule.scheduleRedraw();

        this._compassCircle.setID(ids);
        this._compassCircleBack.setID(ids + 1);
        this._compassPlus.setID(ids + 2);
        this._compassMinus.setID(ids + 3);
    }

    private _initAxesCrossIdsPerView() {

        let ids = Tools.getInstance().getManipulatorManager().getID(this._arrows.length + 3);

        // set same compass id for all views, but whis needs to be changes in future to be able to select each compass
        // in each view separately
        // we need to pass all views and set the arrows enabled
        // TODO: do not access the private mode _mode and _viewMode
        let views = this._sceneAPI.viewModeModule.getViews();
        for (let v in views) {
            let view = views[v];
            view.getHud().setArrowsIDRange([ids, ids + this._arrows.length + 2]);
        }

        this._sceneAPI.viewModeModule.scheduleRedraw();

        for (let i = 0; i < this._arrows.length; ++i) {
            this._arrows[i].setID(ids + i);
        }

        this._letterX.setID(ids + this._arrows.length);
        this._letterY.setID(ids + this._arrows.length + 1);
        this._letterZ.setID(ids + this._arrows.length + 2);
    }

    /**
     * Initialize values
     */
    private _initialize() {
        // Test for proper initialization
        if (!this._sceneAPI.checkModulesInitialized()) {
            return;
        }

        // Test for model loaded
        if (!this._sceneAPI.getSceneLoaded()) {
            return;
        }

        // More initialization after model loaded
        this._initialized = true;

        // Compass manipulator
        this._initCompassIdsPerView();

        // arrows id
        this._initAxesCrossIdsPerView();

        // Background image
        this._backgroundImage = new Plane(this._sceneAPI, (this._context as WebGLContext).getWebGLRenderingContext());
        this._backgroundImage.setRotation(0, Math.PI, 0);
        this._backgroundImage.reserveTexture(0, false, false);
    }

    /**
     * Initialize values
     */
    private _setCompassTextures() {
        let type = this._sceneAPI.hudModule.getCompassType();
        if (type === this._compassType) {
            return;
        }

        this._compassType = type;
        this._compassCircle.setTexture(0, this._compassType === CompassTypeEnum.LIGHT ?
            '@assets/images/environment/geoCompass.png' : '@assets/images/environment/geoCompass_dark.png', false, true, true);

        this._compassCircleBack.setTexture(0, this._compassType === CompassTypeEnum.LIGHT ?
            '@assets/images/environment/geoCompassInverted.png' : '@assets/images/environment/geoCompassInverted_dark.png', false, true, true);

        this._compassPlus.setTexture(0, this._compassType === CompassTypeEnum.LIGHT ?
            '@assets/images/environment/geoCompassPlus.png' : '@assets/images/environment/geoCompassPlus_dark.png', false, true, true);

        this._compassMinus.setTexture(0, this._compassType === CompassTypeEnum.LIGHT ?
            '@assets/images/environment/geoCompassMinus.png' : '@assets/images/environment/geoCompassMinus_dark.png', false, true, true);
    }

    /**
     * Test and initialize if not already yet
     *
     * @return {boolean} true if should render
     * @private
     */
    private _canRender(): boolean {
        // Test for not initialized values
        if (!this._initialized) {
            this._initialize();

            // Check if init was OK
            if (!this._initialized) {
                return false;
            }
        }

        return true;
    }

    /**
     * TODO Add lighting, it looks better
     *
     * @param {Pass} aRenderPass
     * @private
     */
    private _renderArrows(aRenderPass: Pass) {
        /* jshint camelcase:false */
        /** @type WebGLRenderingContext */
        let gl = (this._context as WebGLContext).getWebGLRenderingContext();
        let view = aRenderPass.view;
        let camera = view.getCamera();
        let shader: any = aRenderPass.setShaderByIndex(0, this._typeName);

        vec3.scale(this._pgc.eye, camera.getCameraDirection(), -1);

        // Set uniforms
        gl.uniformMatrix4fv(shader.uniformLocations.u_viewProjectionMatrix, false, view.getHud().getHUDMatrix());

        for (let i = 0; i < this._arrows.length; ++i) {
            let arrow = this._arrows[i];

            // Model rotation matrix
            vec3.set(this._tmpVec3, 0, 0, 0);
            mat4.lookAt(this._tmpMat4, this._pgc.eye, this._tmpVec3, this._pgc.upDir);
            if (i === 0) {
                mat4.rotateX(this._tmpMat4, this._tmpMat4, Math.PI / 2.0);
                mat4.rotateZ(this._tmpMat4, this._tmpMat4, -Math.PI / 2.0);
            } else if (i === 2) {
                mat4.rotateX(this._tmpMat4, this._tmpMat4, -Math.PI / 2.0);
            }

            gl.uniformMatrix4fv(shader.uniformLocations.u_rotationMatrix, false, this._tmpMat4);
            arrow.draw(aRenderPass, undefined);

            // Letter to axis
            vec3.set(this._tmpVec3, 0, 6, 0);
            switch (i) {
                case 0:
                    mat4.translate(this._tmpMat4, this._tmpMat4, this._tmpVec3);
                    gl.uniformMatrix4fv(shader.uniformLocations.u_rotationMatrix, false, this._tmpMat4);
                    this._letterX.draw(aRenderPass, 1);
                    break;
                case 1:
                    mat4.translate(this._tmpMat4, this._tmpMat4, this._tmpVec3);
                    mat4.rotateX(this._tmpMat4, this._tmpMat4, Math.PI / 2.0);
                    gl.uniformMatrix4fv(shader.uniformLocations.u_rotationMatrix, false, this._tmpMat4);
                    this._letterZ.draw(aRenderPass, 1);
                    break;
                case 2:
                    mat4.translate(this._tmpMat4, this._tmpMat4, this._tmpVec3);
                    mat4.rotateX(this._tmpMat4, this._tmpMat4, Math.PI); // rotate the letter back
                    gl.uniformMatrix4fv(shader.uniformLocations.u_rotationMatrix, false, this._tmpMat4);
                    this._letterY.draw(aRenderPass, 1);
                    break;
            }
        }
    }

    /**
     * @param {mat4} matrix Output
     * @param {vec3} modelDirection Model direction from camera
     * @param {boolean} tilt True if compass is rotating only in "2D"
     * @private
     */
    private _calcCompassRotation(matrix: mat4, modelDirection: vec3, tilt: boolean) {
        let compassRotationOffset = this._sceneAPI.hudModule.getCompassRotationOffset();

        vec3.scale(this._pgc.eye, modelDirection, -1);
        if (tilt) {
            vec3.set(this._tmpVec3, 0, 0, 0);
            mat4.lookAt(matrix, this._pgc.eye, this._tmpVec3, this._pgc.upDir);
            mat4.rotateZ(matrix, matrix, Math.PI);
            mat4.rotateY(matrix, matrix, compassRotationOffset - (Math.PI / 2.0));
            mat4.rotateX(matrix, matrix, -Math.PI / 2.0);
        } else {
            let angleX = Math.atan2(this._pgc.eye[2], this._pgc.eye[0]);
            mat4.identity(matrix);
            if (this._pgc.eye[1] >= 0) {
                mat4.rotateY(matrix, matrix, Math.PI);
                mat4.rotateZ(matrix, matrix, -angleX + compassRotationOffset);
            } else {
                // mat4.rotateY(this._tmpMat4, this._tmpMat4, Math.PI);
                mat4.rotateZ(matrix, matrix, -angleX + compassRotationOffset);
            }
        }
    }

    /**
     * @param {Pass} aRenderPass
     * @private
     */
    private _renderCompass(aRenderPass: Pass) {
        /* jshint camelcase:false */
        /** @type WebGLRenderingContext */
        let gl = (this._context as WebGLContext).getWebGLRenderingContext();
        let view = aRenderPass.view;
        let camera = view.getCamera();

        this._compassCircle.setVisible(true);
        let shader: any = aRenderPass.setShaderByIndex(1, this._typeName);

        // Blending for grass billboards
        // gl.enable(gl.SAMPLE_ALPHA_TO_COVERAGE);
        gl.enable(gl.BLEND);
        gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

        // Set uniforms
        gl.uniformMatrix4fv(shader.uniformLocations.u_viewProjectionMatrix, false, view.getHud().getHUDMatrix());

        let hoverID = view.getHud().getHoverID();
        let tilt = view.getHud().isCompassTiltEnabled();

        // Rotating compass
        this._calcCompassRotation(this._tmpMat4, camera.getCameraDirection(), tilt);
        gl.uniformMatrix4fv(shader.uniformLocations.u_rotationMatrix, false, this._tmpMat4);

        if (hoverID === this._compassCircle.getID()) {
            this._compassCircle.setAlpha(this._compassOn);
        } else {
            this._compassCircle.setAlpha(this._compassOff);
        }

        // Draw front
        this._compassCircle.draw(aRenderPass, 1);

        // Rotate, draw back
        mat4.rotateY(this._tmpMat4, this._tmpMat4, Math.PI);
        gl.uniformMatrix4fv(shader.uniformLocations.u_rotationMatrix, false, this._tmpMat4);
        if (hoverID === this._compassCircleBack.getID()) {
            this._compassCircleBack.setAlpha(this._compassOn);
        } else {
            this._compassCircleBack.setAlpha(this._compassOff);
        }
        this._compassCircleBack.draw(aRenderPass, 1);

        // Without rotation buttons
        mat4.identity(this._tmpMat4);
        gl.uniformMatrix4fv(shader.uniformLocations.u_rotationMatrix, false, this._tmpMat4);

        if (hoverID === this._compassPlus.getID()) {
            this._compassPlus.setAlpha(this._compassOn);
        } else {
            this._compassPlus.setAlpha(this._compassOff);
        }
        this._compassPlus.draw(aRenderPass, 1);

        if (hoverID === this._compassMinus.getID()) {
            this._compassMinus.setAlpha(this._compassOn);
        } else {
            this._compassMinus.setAlpha(this._compassOff);
        }
        this._compassMinus.draw(aRenderPass, 1);

        gl.disable(gl.BLEND);
        shader.disableAttributes();
    }
}

Factory.register(HUD);
